"use client";
// src/components/profile/ReviewSubmitForm.tsx

import { useEffect, useMemo, useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { submitReviewAction } from "@/app/actions/reviews";
import { useUser } from "@/context/UserContext";
import { GUEST_STAY_CATEGORY_KEYS, GUEST_STAY_REVIEW_TAGS, type GuestStayCategoryKey, type GuestStayReviewTag } from "@/types/review";

interface ReviewSubmitFormProps {
  profileId: string;
  profileOwnerName: string;
}

const guestCategoryLabels: Record<GuestStayCategoryKey, string> = {
  accuracy: "Accuracy",
  cleanliness: "Cleanliness",
  communication: "Communication",
  checkin: "Check-in",
  location: "Location",
  value: "Value",
};

const guestTagLabels: Record<GuestStayReviewTag, string> = {
  great_value: "Great value",
  very_clean: "Very clean",
  great_location: "Great location",
  smooth_checkin: "Smooth check-in",
  great_communication: "Great communication",
  matches_listing: "Matches listing",
};

const defaultGuestCategoryRatings = (): Record<GuestStayCategoryKey, number> =>
  GUEST_STAY_CATEGORY_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {} as Record<GuestStayCategoryKey, number>);

type GuestReviewTask = {
  bookingId: string;
  status: "not_open_yet" | "open_not_started" | "submitted_waiting" | "published" | "expired_no_submission";
  opensAt: string;
  closesAt: string;
};

const reviewTaskLabelMap: Record<GuestReviewTask["status"], { label: string; tone: string }> = {
  not_open_yet: { label: "Review opens soon", tone: "text-amber-700" },
  open_not_started: { label: "Review needed", tone: "text-emerald-700" },
  submitted_waiting: { label: "Submitted, waiting for release", tone: "text-blue-700" },
  published: { label: "Published", tone: "text-emerald-700" },
  expired_no_submission: { label: "Window closed", tone: "text-muted-foreground" },
};


export function ReviewSubmitForm({ profileId, profileOwnerName }: ReviewSubmitFormProps) {
  const { firebaseUser } = useUser();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [body, setBody] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerTitle, setReviewerTitle] = useState("");
  const [guestCategoryRatings, setGuestCategoryRatings] = useState<Record<GuestStayCategoryKey, number>>(() => defaultGuestCategoryRatings());
  const [stayTags, setStayTags] = useState<GuestStayReviewTag[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [tasks, setTasks] = useState<GuestReviewTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadTasks = async () => {
      if (!firebaseUser) {
        setTasks([]);
        return;
      }
      setTasksLoading(true);
      try {
        const token = await firebaseUser.getIdToken();
        const response = await fetch(`/api/stay/reviews/tasks?profileId=${encodeURIComponent(profileId)}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as { tasks?: GuestReviewTask[] };
        if (!cancelled) {
          setTasks(Array.isArray(payload.tasks) ? payload.tasks : []);
        }
      } catch {
        if (!cancelled) {
          setTasks([]);
        }
      } finally {
        if (!cancelled) {
          setTasksLoading(false);
        }
      }
    };

    void loadTasks();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser, profileId]);

  const nextTask = useMemo(
    () => tasks.find((task) => task.status === "open_not_started" || task.status === "not_open_yet") || tasks[0],
    [tasks],
  );

  const toggleStayTag = (tag: GuestStayReviewTag) => {
    setStayTags((current) => (
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag]
    ));
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!firebaseUser) {
      setResult({ success: false, message: "Please sign in first. Only verified guests can leave a star rating." });
      return;
    }
    if (rating === 0) {
      setResult({ success: false, message: "Please choose a star rating." });
      return;
    }
    if (!reviewerName.trim()) {
      setResult({ success: false, message: "Please enter your first name." });
      return;
    }
    const unratedCategories = GUEST_STAY_CATEGORY_KEYS.filter(
      (key) => !(guestCategoryRatings[key] >= 1 && guestCategoryRatings[key] <= 5),
    );
    if (unratedCategories.length > 0) {
      setResult({
        success: false,
        message: `Please rate the following criteria before submitting: ${unratedCategories.map((key) => guestCategoryLabels[key]).join(", ")}.`,
      });
      return;
    }
    setSubmitting(true);
    setResult(null);
    const res = await submitReviewAction(profileId, {
      rating,
      body,
      reviewerName: reviewerName.trim(),
      reviewerTitle: reviewerTitle.trim() || undefined,
      guestCategoryRatings,
      stayTags,
    });
    setResult(res);
    setSubmitting(false);
    if (res.success) {
      setRating(0);
      setBody("");
      setReviewerName("");
      setReviewerTitle("");
      setGuestCategoryRatings(defaultGuestCategoryRatings());
      setStayTags([]);
    }
  }

  if (result?.success) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
        {result.message}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {firebaseUser && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
          <p className="text-sm font-semibold text-emerald-900">Your review task</p>
          {tasksLoading ? (
            <p className="mt-1 text-xs text-emerald-800">Checking your review window…</p>
          ) : nextTask ? (
            <>
              <p className={`mt-1 text-xs font-medium ${reviewTaskLabelMap[nextTask.status].tone}`}>
                {reviewTaskLabelMap[nextTask.status].label}
              </p>
              {nextTask.status === "open_not_started" && (
                <p className="mt-1 text-xs text-emerald-800">
                  Submit before {new Date(nextTask.closesAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })} to keep your voice in this booking's trust record.
                </p>
              )}
              {nextTask.status === "not_open_yet" && (
                <p className="mt-1 text-xs text-emerald-800">
                  Your review opens {new Date(nextTask.opensAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}.
                </p>
              )}
            </>
          ) : (
            <p className="mt-1 text-xs text-emerald-800">No active review task yet for this stay profile.</p>
          )}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Easy rating flow: sign in, choose stars, and submit. Reviews are accepted for completed, paid bookings inside the 14-day review window.
      </p>
      <p className="text-xs text-muted-foreground">
        Public display format: first name and surname initial (example: Sarah M.). Double-blind release rules apply.
      </p>
      <p className="text-xs font-medium text-emerald-700">
        Estimated completion time: under 45 seconds.
      </p>
      <div>
        <p className="mb-1 text-sm font-medium text-foreground">
          Your rating for {profileOwnerName}
        </p>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="focus:outline-none"
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  star <= (hovered || rating)
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-gray-300"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
        <p className="text-xs font-medium text-slate-900">Quick criteria</p>
        <p className="mt-1 text-xs text-slate-600">Rate each criterion honestly — the overall score reflects your real experience, not a default 5/5.</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {GUEST_STAY_CATEGORY_KEYS.map((key) => (
            <label key={key} className="block">
              <span className="mb-1 block text-xs font-medium text-foreground">{guestCategoryLabels[key]}</span>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={guestCategoryRatings[key]}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setGuestCategoryRatings((current) => ({ ...current, [key]: value }));
                }}
                disabled={submitting}
              >
                <option value={0}>— Select rating —</option>
                {[5, 4, 3, 2, 1].map((value) => (
                  <option key={value} value={value}>{value} / 5</option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="reviewer-name" className="mb-1 block text-sm font-medium text-foreground">
          Your first name <span className="text-destructive">*</span>
        </label>
        <Input
          id="reviewer-name"
          placeholder="e.g. Sarah M"
          value={reviewerName}
          onChange={(e) => setReviewerName(e.target.value)}
          maxLength={40}
          required
          className="max-w-xs"
        />
      </div>

      <div>
        <label htmlFor="review-title" className="mb-1 block text-sm font-medium text-foreground">
          Your title or role <span className="text-muted-foreground">(optional)</span>
        </label>
        <Input
          id="review-title"
          placeholder="e.g. Restaurant owner, Business client"
          value={reviewerTitle}
          onChange={(e) => setReviewerTitle(e.target.value)}
          maxLength={80}
          className="max-w-xs"
        />
      </div>

      <div>
        <label htmlFor="review-body" className="mb-1 block text-sm font-medium text-foreground">
          Your review
        </label>
        <Textarea
          id="review-body"
          placeholder="Share your experience. What stood out? Who would you recommend this to?"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          rows={4}
          required
        />
        <p className="mt-1 text-xs text-muted-foreground">{body.length}/500</p>
      </div>

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <p className="text-sm font-medium text-foreground">Highlight tags (optional)</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {GUEST_STAY_REVIEW_TAGS.map((tag) => (
            <label key={tag} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={stayTags.includes(tag)}
                onChange={() => toggleStayTag(tag)}
                disabled={submitting}
              />
              <span>{guestTagLabels[tag]}</span>
            </label>
          ))}
        </div>
      </div>

      {result && !result.success && (
        <p className="text-sm text-destructive">{result.message}</p>
      )}

      <Button type="submit" disabled={submitting || !firebaseUser} className="w-full sm:w-auto">
        {submitting ? "Submitting…" : "Submit Review"}
      </Button>
    </form>
  );
}
